import React, { useEffect, useState } from "react";
import { auth } from "../lib/firebase";
import { onAuthStateChanged } from "firebase/auth";
import { getUserRole, getPageConfig, savePageConfig, DEFAULT_CONFIG, getCachedStops } from "../lib/config";
import { fetchStops, fetchArrivals, SYSTEM_ID } from "../lib/api";
import { useParams, useNavigate } from "react-router-dom";
import SearchableStopPicker from "./SearchableStopPicker";
import BusConfigurator from "./BusConfigurator";

function Settings() {
    const { pageId } = useParams();
    const navigate = useNavigate();
    const [user, setUser] = useState(null);
    const [config, setConfig] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);
    const [error, setError] = useState(null);
    const [stops, setStops] = useState([]);
    const [availableRoutes, setAvailableRoutes] = useState([]);
    const [loadingRoutes, setLoadingRoutes] = useState(false);

    useEffect(() => {
        const unsub = onAuthStateChanged(auth, async (u) => {
            if (!u) { navigate("/"); return; }
            const role = await getUserRole(u.uid);
            if (!role || role.status !== "approved") {
                navigate("/");
                return;
            }
            setUser(u);
        });
        return unsub;
    }, [navigate]);

    useEffect(() => {
        if (!user) return;
        async function load() {
            setLoading(true);
            try {
                const pageData = await getPageConfig(pageId);
                if (!pageData) {
                    setError("Page not found");
                    setLoading(false);
                    return;
                }
                setConfig({
                    subwayStopId: pageData.subwayStopId || DEFAULT_CONFIG.subwayStopId,
                    subwayRoutes: pageData.subwayRoutes || DEFAULT_CONFIG.subwayRoutes,
                    busConfigs: pageData.busConfigs || DEFAULT_CONFIG.busConfigs,
                    transfers: pageData.transfers || DEFAULT_CONFIG.transfers,
                });
            } catch (err) {
                setError(err.message);
            }
            setLoading(false);
        }
        load();
    }, [user, pageId]);

    useEffect(() => {
        getCachedStops(SYSTEM_ID, fetchStops)
            .then((stops) => setStops(stops))
            .catch(() => setStops([]));
    }, []);

    // Fetch available routes when subway stop changes
    useEffect(() => {
        if (!config?.subwayStopId) {
            setAvailableRoutes([]);
            return;
        }
        setLoadingRoutes(true);
        fetchArrivals(SYSTEM_ID, config.subwayStopId)
            .then((data) => {
                // Extract unique routes from serviceMaps
                const routeMap = new Map();
                (data.serviceMaps || []).forEach((sm) => {
                    (sm.routes || []).forEach((r) => {
                        if (!routeMap.has(r.id)) {
                            routeMap.set(r.id, { id: r.id, color: r.color });
                        }
                    });
                });
                setAvailableRoutes(Array.from(routeMap.values()));
            })
            .catch(() => setAvailableRoutes([]))
            .finally(() => setLoadingRoutes(false));
    }, [config?.subwayStopId]);

    async function handleSave() {
        setSaving(true);
        setSaved(false);
        setError(null);
        try {
            await savePageConfig(pageId, config, user.uid);
            setSaved(true);
            setTimeout(() => setSaved(false), 2000);
        } catch (err) {
            setError(err.message);
        }
        setSaving(false);
    }

    // ---- Config update helpers ----
    function updateField(field, value) {
        setConfig((c) => ({ ...c, [field]: value }));
    }

    function toggleSubwayRoute(routeId, color) {
        setConfig((c) => {
            const exists = c.subwayRoutes.find((r) => r.routeId === routeId);
            if (exists) {
                return { ...c, subwayRoutes: c.subwayRoutes.filter((r) => r.routeId !== routeId) };
            } else {
                return {
                    ...c,
                    subwayRoutes: [...c.subwayRoutes, { routeId, directionId: true, label: "To Manhattan", color }],
                };
            }
        });
    }

    function updateSubwayRoute(idx, field, value) {
        setConfig((c) => ({
            ...c,
            subwayRoutes: c.subwayRoutes.map((r, i) => {
                if (i !== idx) return r;
                const updated = { ...r, [field]: value };
                // Auto-update label when direction changes
                if (field === "directionId") {
                    const defaultLabel = value ? "To Manhattan" : "To Brooklyn";
                    if (!r.label || r.label === "To Manhattan" || r.label === "To Brooklyn") {
                        updated.label = defaultLabel;
                    }
                }
                return updated;
            }),
        }));
    }

    function addTransferGroup() {
        setConfig((c) => ({
            ...c,
            transfers: [...c.transfers, { transferStopId: "", routes: [] }],
        }));
    }

    function removeTransferGroup(idx) {
        setConfig((c) => ({
            ...c,
            transfers: c.transfers.filter((_, i) => i !== idx),
        }));
    }

    function updateTransferStopId(idx, value) {
        setConfig((c) => ({
            ...c,
            transfers: c.transfers.map((t, i) =>
                i === idx ? { ...t, transferStopId: value, routes: [] } : t
            ),
        }));
    }

    function toggleTransferRoute(groupIdx, routeId, color) {
        setConfig((c) => ({
            ...c,
            transfers: c.transfers.map((t, i) => {
                if (i !== groupIdx) return t;
                const exists = t.routes.find((r) => r.routeId === routeId);
                if (exists) {
                    return { ...t, routes: t.routes.filter((r) => r.routeId !== routeId) };
                } else {
                    return { ...t, routes: [...t.routes, { routeId, directionId: true, color, label: "To Manhattan" }] };
                }
            }),
        }));
    }

    function updateTransferRouteDirection(groupIdx, routeId, directionId) {
        setConfig((c) => ({
            ...c,
            transfers: c.transfers.map((t, i) => {
                if (i !== groupIdx) return t;
                return {
                    ...t,
                    routes: t.routes.map((r) => {
                        if (r.routeId !== routeId) return r;
                        const updated = { ...r, directionId };
                        if (!r.label || r.label === "To Manhattan" || r.label === "To Brooklyn") {
                            updated.label = directionId ? "To Manhattan" : "To Brooklyn";
                        }
                        return updated;
                    }),
                };
            }),
        }));
    }

    function updateTransferRouteLabel(groupIdx, routeId, label) {
        setConfig((c) => ({
            ...c,
            transfers: c.transfers.map((t, i) => {
                if (i !== groupIdx) return t;
                return {
                    ...t,
                    routes: t.routes.map((r) =>
                        r.routeId === routeId ? { ...r, label } : r
                    ),
                };
            }),
        }));
    }

    if (loading) {
        return (
            <div className="settings-page">
                <div className="settings-loading">Loading...</div>
            </div>
        );
    }

    if (error && !config) {
        return (
            <div className="settings-page">
                <div className="settings-error">{error}</div>
                <button className="text-btn" onClick={() => navigate("/dashboard")}>← Back to Dashboard</button>
            </div>
        );
    }

    if (!config) return null;

    return (
        <div className="settings-page">
            <div className="settings-header">
                <button className="text-btn" onClick={() => navigate("/dashboard")}>← Dashboard</button>
                <h1>Settings for /{pageId}</h1>
                <div className="settings-header-actions">
                    <button className="secondary-btn" onClick={() => navigate(`/${pageId}`, { state: { previewConfig: config } })}>
                        👁️ Preview
                    </button>
                    <button className="primary-btn" onClick={handleSave} disabled={saving}>
                        {saving ? "Saving..." : saved ? "✓ Saved!" : "💾 Save"}
                    </button>
                </div>
            </div>

            {error && <p className="error-text">{error}</p>}

            {/* ===== SUBWAY SECTION ===== */}
            <section className="settings-section">
                <h2>🚇 Subway</h2>
                <div className="settings-field">
                    <label>Stop</label>
                    <SearchableStopPicker
                        stops={stops}
                        value={config.subwayStopId}
                        onChange={(id) => updateField("subwayStopId", id)}
                        placeholder="Search subway stops..."
                    />
                </div>

                <div className="settings-field">
                    <label>Available Routes (click to toggle)</label>
                    {loadingRoutes && <p style={{ color: '#888', fontSize: '0.9rem' }}>Loading routes...</p>}
                    {!loadingRoutes && availableRoutes.length === 0 && config.subwayStopId && (
                        <p style={{ color: '#888', fontSize: '0.9rem' }}>No routes found for this stop.</p>
                    )}
                    <div className="route-toggle-grid">
                        {availableRoutes.map((ar) => {
                            const isOn = config.subwayRoutes.some((r) => r.routeId === ar.id);
                            return (
                                <button
                                    key={ar.id}
                                    className={`route-toggle-chip ${isOn ? 'active' : ''}`}
                                    style={isOn ? { background: ar.color ? `#${ar.color}` : '#333', color: '#fff', borderColor: 'transparent' } : {}}
                                    onClick={() => toggleSubwayRoute(ar.id, ar.color)}
                                >
                                    {ar.id}
                                </button>
                            );
                        })}
                    </div>
                </div>

                {config.subwayRoutes.length > 0 && (
                    <div className="settings-field">
                        <label>Direction for selected routes</label>
                        {config.subwayRoutes.map((route, idx) => (
                            <div key={route.routeId} className="settings-row">
                                <span className="route-badge-mini" style={{ background: route.color ? `#${route.color}` : '#333' }}>
                                    {route.routeId}
                                </span>
                                <select
                                    value={route.directionId ? "true" : "false"}
                                    onChange={(e) => updateSubwayRoute(idx, "directionId", e.target.value === "true")}
                                >
                                    <option value="true">Uptown / Manhattan-bound</option>
                                    <option value="false">Downtown / Brooklyn-bound</option>
                                </select>
                                <input
                                    type="text"
                                    placeholder="Label"
                                    value={route.label || ""}
                                    onChange={(e) => updateSubwayRoute(idx, "label", e.target.value)}
                                    className="settings-input-md"
                                />
                            </div>
                        ))}
                    </div>
                )}
            </section>

            <section className="settings-section">
                <h2>🚌 Bus</h2>
                <BusConfigurator
                    busConfigs={config.busConfigs || []}
                    onChange={(busConfigs) => setConfig((c) => ({ ...c, busConfigs }))}
                />
            </section>

            {/* ===== TRANSFERS SECTION ===== */}
            <section className="settings-section">
                <h2>🔄 Transfers</h2>
                <p className="settings-hint">
                    Transfer connections show wait times for connecting routes when your subway arrives at a transfer station.
                </p>
                {config.transfers.map((group, gIdx) => (
                    <TransferGroupCard
                        key={gIdx}
                        group={group}
                        gIdx={gIdx}
                        stops={stops}
                        onStopChange={(id) => updateTransferStopId(gIdx, id)}
                        onToggleRoute={(routeId, color) => toggleTransferRoute(gIdx, routeId, color)}
                        onDirectionChange={(routeId, dir) => updateTransferRouteDirection(gIdx, routeId, dir)}
                        onLabelChange={(routeId, label) => updateTransferRouteLabel(gIdx, routeId, label)}
                        onRemove={() => removeTransferGroup(gIdx)}
                    />
                ))}
                <button className="add-btn" onClick={addTransferGroup}>+ Add Transfer Station</button>
            </section>

            <div className="settings-footer">
                <button className="primary-btn large" onClick={handleSave} disabled={saving}>
                    {saving ? "Saving..." : saved ? "✓ Saved!" : "💾 Save Settings"}
                </button>
            </div>
        </div>
    );
}

export default Settings;

// Sub-component for each transfer group — has its own route-fetching state
function TransferGroupCard({ group, gIdx, stops, onStopChange, onToggleRoute, onDirectionChange, onLabelChange, onRemove }) {
    const [availRoutes, setAvailRoutes] = useState([]);
    const [loadingRoutes, setLoadingRoutes] = useState(false);

    useEffect(() => {
        if (!group.transferStopId) {
            setAvailRoutes([]);
            return;
        }
        setLoadingRoutes(true);
        fetchArrivals(SYSTEM_ID, group.transferStopId)
            .then((data) => {
                const routeMap = new Map();
                (data.serviceMaps || []).forEach((sm) => {
                    (sm.routes || []).forEach((r) => {
                        if (!routeMap.has(r.id)) {
                            routeMap.set(r.id, { id: r.id, color: r.color });
                        }
                    });
                });
                setAvailRoutes(Array.from(routeMap.values()));
            })
            .catch(() => setAvailRoutes([]))
            .finally(() => setLoadingRoutes(false));
    }, [group.transferStopId]);

    return (
        <div className="transfer-group-card">
            <div className="transfer-group-header">
                <div className="settings-field" style={{ flex: 1, marginBottom: 0 }}>
                    <label>Transfer Stop</label>
                    <SearchableStopPicker
                        stops={stops}
                        value={group.transferStopId}
                        onChange={onStopChange}
                        placeholder="Search transfer stops..."
                    />
                </div>
                <button className="icon-btn danger" onClick={onRemove} title="Remove group">✕</button>
            </div>

            {group.transferStopId && (
                <>
                    <div className="settings-field" style={{ marginTop: 12 }}>
                        <label>Available Routes (click to toggle)</label>
                        {loadingRoutes && <p style={{ color: '#888', fontSize: '0.9rem' }}>Loading routes...</p>}
                        <div className="route-toggle-grid">
                            {availRoutes.map((ar) => {
                                const isOn = group.routes.some((r) => r.routeId === ar.id);
                                return (
                                    <button
                                        key={ar.id}
                                        className={`route-toggle-chip ${isOn ? 'active' : ''}`}
                                        style={isOn ? { background: ar.color ? `#${ar.color}` : '#333', color: '#fff', borderColor: 'transparent' } : {}}
                                        onClick={() => onToggleRoute(ar.id, ar.color)}
                                    >
                                        {ar.id}
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {group.routes.length > 0 && (
                        <div className="settings-field" style={{ marginTop: 8 }}>
                            <label>Direction for selected routes</label>
                            {group.routes.map((route) => (
                                <div key={route.routeId} className="settings-row">
                                    <span className="route-badge-mini" style={{ background: route.color ? `#${route.color}` : '#333' }}>
                                        {route.routeId}
                                    </span>
                                    <select
                                        value={route.directionId ? "true" : "false"}
                                        onChange={(e) => onDirectionChange(route.routeId, e.target.value === "true")}
                                    >
                                        <option value="true">Uptown / Manhattan-bound</option>
                                        <option value="false">Downtown / Brooklyn-bound</option>
                                    </select>
                                    <input
                                        type="text"
                                        placeholder="Label"
                                        value={route.label || ""}
                                        onChange={(e) => onLabelChange(route.routeId, e.target.value)}
                                        className="settings-input-md"
                                    />
                                </div>
                            ))}
                        </div>
                    )}
                </>
            )}
        </div>
    );
}
